import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { User, Briefcase, Edit, Mail, Linkedin, Phone, MapPin, UserPlus, BookOpen, Ban, Lock, GraduationCap, Users, Video, Calendar } from "lucide-react";
import { MentorInitiatedRequestButton } from "@/components/mentorship/MentorInitiatedRequestButton";
import { format } from "date-fns";
import { useSharedContactInfo } from "@/hooks/useSharedContactInfo";
import { CancelMentorshipRequestDialog } from "@/components/mentorship/CancelMentorshipRequestDialog";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { getEducationLabel } from "@/lib/educationLevels";
import { getCompanyStageLabel } from "@/lib/companyStages";
import { FoundationsRequiredBanner } from "@/components/mentorship/FoundationsRequiredBanner";
import { ConnectionButton } from "@/components/connections/ConnectionButton";
import { useAreUsersConnected, useConnectionType, useHasActiveMentorship, useConnectionDetails } from "@/hooks/useConnectionStatus";
import { useAuth } from "@/lib/auth";
import { useGrantDualRole } from "@/hooks/useDualRoleRequestsAdmin";
import { useHasRole } from "@/hooks/useUserRoles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { UserName } from "@/components/badges/UserName";
import { UserBadgesSection } from "@/components/badges/UserBadgesSection";

interface MemberProfile {
  id: string;
  full_name: string;
  avatar_url: string | null;
  bio: string | null;
  role: string;
  email?: string | null;
  linkedin_url?: string | null;
  parish?: string | null;
  phone?: string | null;
  phone_type?: string | null;
  referred_by?: string | null;
  gender?: string | null;
  age_range?: string | null;
  has_device?: string | null;
  education_level?: string | null;
  is_published?: boolean;
  is_verified?: boolean;
  is_onboarded?: boolean;
  is_mentor?: boolean;
  is_entrepreneur?: boolean;
  created_at?: string | null;
  skills: { name: string }[];
  industries: { name: string }[];
  entrepreneur_profile?: {
    company_name: string | null;
    company_description: string | null;
    startup_stage: string | null;
    target_market: string | null;
    funding_status: string | null;
    specific_challenges: string | null;
  } | null;
  mentor_profile?: {
    years_experience: number | null;
    professional_background: string | null;
    career_highlights: string | null;
    availability_notes: string | null;
  } | null;
}

interface ProfileDetailDialogProps {
  member: MemberProfile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showAllFields?: boolean;
  showContactInfo?: boolean;
  onEdit?: () => void;
  canRequestConnection?: boolean;
  onRequestConnection?: () => void;
  connectionStatus?: string | null;
  /** Whether to show the mentorship button (basic eligibility - is mentor, not self, no existing request) */
  canRequestMentorship?: boolean;
  /** Whether the user has completed the Foundations course (entrepreneur only) */
  hasCompletedFoundations?: boolean;
  /** Whether the user is enrolled in the Foundations course */
  isEnrolledInFoundations?: boolean;
  /** Progress in the Foundations course (0-100) */
  foundationsProgress?: number;
  /** Link to the Foundations course */
  foundationsCourseLink?: string;
  onRequestMentorship?: () => void;
  mentorshipRequestStatus?: string | null;
  mentorshipRequestId?: string | null;
  onCancelMentorshipRequest?: () => void;
}

// Helper component for locked fields
function LockedField({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md border border-dashed border-muted-foreground/30">
      <Lock className="h-3 w-3 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

export function ProfileDetailDialog({
  member,
  open,
  onOpenChange,
  showAllFields = false,
  showContactInfo = false,
  onEdit,
  canRequestConnection = false,
  onRequestConnection,
  connectionStatus = null,
  canRequestMentorship = false,
  hasCompletedFoundations = true,
  isEnrolledInFoundations = false,
  foundationsProgress = 0,
  foundationsCourseLink = "/courses",
  onRequestMentorship,
  mentorshipRequestStatus = null,
  mentorshipRequestId = null,
  onCancelMentorshipRequest,
}: ProfileDetailDialogProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = useHasRole('admin');
  const [confirmRevokeRole, setConfirmRevokeRole] = useState<'mentor' | 'entrepreneur' | null>(null);
  const grantDualRole = useGrantDualRole();
  const queryClient = useQueryClient();
  const revokeDualRole = useMutation({
    mutationFn: async ({ userId, roleToRevoke }: { userId: string; roleToRevoke: 'mentor' | 'entrepreneur' }) => {
      const { error } = await supabase.rpc('revoke_dual_role', {
        p_user_id: userId,
        p_role_to_revoke: roleToRevoke,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_profiles'] });
      queryClient.invalidateQueries({ queryKey: ['profile_review_full'] });
      queryClient.invalidateQueries({ queryKey: ['pending_profiles_review'] });
      queryClient.invalidateQueries({ queryKey: ['dual_role_requests_admin'] });
      toast.success('Role capability revoked');
    },
    onError: (error: Error) => {
      toast.error('Failed to revoke role: ' + error.message);
    },
  });
  
  // Check connection status using unified connections system
  const { data: isConnected, isLoading: connectionLoading } = useAreUsersConnected(
    open ? member?.id : null
  );
  
  // Get connection type (community or mentorship)
  const { data: connectionType } = useConnectionType(
    open && isConnected ? member?.id : null
  );
  
  // Check for active mentorship
  const { data: hasActiveMentorship } = useHasActiveMentorship(
    open && isConnected ? member?.id : null
  );
  
  // Get full connection details for scheduled meetings/zoom links
  const { data: connectionDetails } = useConnectionDetails(
    open && isConnected ? member?.id : null
  );
  
  // Fetch shared contact info via RPC when dialog is open
  const { data: sharedContact, isLoading: contactLoading } = useSharedContactInfo(
    open && showContactInfo ? member?.id : null
  );
  const avatarUrl = useStorageUrl("avatars", member?.avatar_url);

  if (!member) return null;

  const isOwnProfile = user?.id === member.id;
  const isMentor = member.role === "mentor" || member.is_mentor === true;
  const isEntrepreneur = member.role === "entrepreneur";
  
  // Determine relationship label - ONLY for active mentorships
  const getMentorshipLabel = () => {
    // Only show mentor/mentee labels for mentorship connections
    if (connectionType !== 'mentorship') return null;
    
    // If mentorship is not active, show "Former" labels
    if (hasActiveMentorship === false) {
      if (isMentor) return 'Former Mentor';
      return 'Former Mentee';
    }
    
    // Only show "Your Mentor/Mentee" for active mentorships
    if (hasActiveMentorship) {
      if (isMentor) return 'Your Mentor';
      return 'Your Mentee';
    }
    
    return null;
  };
  
  // Determine if user can view private fields
  // Private fields: gender, age_range, education_level, pitch_deck_url, referred_by, has_device
  // Visible if: own profile, connected with user, or is admin
  const canViewPrivateFields = isOwnProfile || showAllFields || isConnected;

  // Use shared contact info if available, otherwise fall back to member data (for admins)
  const contactInfo = sharedContact?.access_level !== "none" ? {
    email: sharedContact?.email ?? member.email,
    phone: sharedContact?.phone ?? member.phone,
    phone_type: sharedContact?.phone_type ?? member.phone_type,
    linkedin_url: sharedContact?.linkedin_url ?? member.linkedin_url,
    parish: sharedContact?.parish ?? member.parish,
  } : {
    email: member.email,
    phone: member.phone,
    phone_type: member.phone_type,
    linkedin_url: member.linkedin_url,
    parish: member.parish,
  };

  const hasContactAccess = showAllFields || (sharedContact?.access_level && sharedContact.access_level !== "none");
  
  // Check for upcoming meeting
  const hasUpcomingMeeting = connectionDetails?.scheduled_at && 
    new Date(connectionDetails.scheduled_at) > new Date();
  
  const mentorshipLabel = getMentorshipLabel();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="sr-only">Profile Details</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[75vh] pr-4">
          <div className="space-y-6">
            {/* Connection Status Banner */}
            {!isOwnProfile && !showAllFields && (
              <div>
                {isConnected ? (
                  <Alert className={connectionType === 'mentorship' 
                    ? "border-amber-500/20 bg-amber-500/5" 
                    : "border-success/20 bg-success/5"
                  }>
                    {connectionType === 'mentorship' ? (
                      <GraduationCap className="h-4 w-4 text-amber-600" />
                    ) : (
                      <Users className="h-4 w-4 text-success" />
                    )}
                    <AlertDescription className={connectionType === 'mentorship' ? "text-amber-700" : "text-success"}>
                      <div className="flex items-center justify-between">
                        <span>
                          {connectionType === 'mentorship' 
                            ? `${mentorshipLabel} - ${hasActiveMentorship ? 'Active Mentorship' : 'Mentorship Connection'}`
                            : `You're connected with ${member.full_name}`
                          }
                        </span>
                        {hasActiveMentorship && (
                          <Badge className="bg-green-500 text-white ml-2">Active</Badge>
                        )}
                      </div>
                      {connectionDetails?.accepted_at && (
                        <p className="text-xs mt-1 opacity-75">
                          Connected since {format(new Date(connectionDetails.accepted_at), 'MMM d, yyyy')}
                        </p>
                      )}
                    </AlertDescription>
                  </Alert>
                ) : !connectionLoading && (
                  <Alert className="border-primary/20 bg-primary/5">
                    <Lock className="h-4 w-4 text-primary" />
                    <AlertDescription>
                      Connect with {member.full_name} to view their full profile and unlock collaboration
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            
            {/* Mentorship Meeting Info - Show when connected via mentorship */}
            {isConnected && connectionType === 'mentorship' && (hasUpcomingMeeting || connectionDetails?.zoom_link) && (
              <Alert className="border-primary/20 bg-primary/5">
                <Calendar className="h-4 w-4 text-primary" />
                <AlertDescription>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    {hasUpcomingMeeting && connectionDetails?.scheduled_at && (
                      <span>Next Meeting: {format(new Date(connectionDetails.scheduled_at), 'MMM d, yyyy h:mm a')}</span>
                    )}
                    {connectionDetails?.zoom_link && (
                      <a 
                        href={connectionDetails.zoom_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                      >
                        <Video className="h-4 w-4" />
                        Join Meeting
                      </a>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Header Section */}
            <div className="flex items-start gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {member.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold"><UserName userId={member.id} name={member.full_name} /></h2>
                  <Badge 
                    variant={isMentor ? "default" : "secondary"}
                    className="capitalize"
                  >
                    {member.role}
                  </Badge>
                  {/* Connection type badge */}
                  {!isOwnProfile && isConnected && connectionType === 'mentorship' && (
                    <Badge className="bg-amber-500 text-white">
                      <GraduationCap className="h-3 w-3 mr-1" />
                      {mentorshipLabel}
                    </Badge>
                  )}
                  {!isOwnProfile && isConnected === true && connectionType === 'community' && (
                    <Badge variant="outline" className="text-primary border-primary">
                      <Users className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                  )}
                  {!isOwnProfile && !isConnected && !connectionLoading && (
                    <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30">
                      Not Connected
                    </Badge>
                  )}
                  {showAllFields && member.is_published === false && (
                    <Badge variant="outline" className="text-orange-500 border-orange-500">
                      Unpublished
                    </Badge>
                  )}
                  {showAllFields && member.is_verified && (
                    <Badge className="bg-green-600 text-white">Verified</Badge>
                  )}
                  {showAllFields && member.is_onboarded && (
                    <Badge className="bg-blue-600 text-white">Onboarded</Badge>
                  )}
                </div>
                {isMentor && member.mentor_profile?.years_experience && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <User className="h-4 w-4" />
                    {member.mentor_profile.years_experience}+ years experience
                  </p>
                )}
                {isEntrepreneur && member.entrepreneur_profile?.company_name && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Briefcase className="h-4 w-4" />
                    {member.entrepreneur_profile.company_name}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 items-end">
                {/* Admin edit button */}
                {showAllFields && onEdit && (
                  <Button variant="outline" size="sm" onClick={onEdit}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
                
                {/* Community Connection Button - only show if not own profile and not admin view */}
                {!isOwnProfile && !showAllFields && (
                  <ConnectionButton
                    targetUserId={member.id}
                    targetUserRole={isMentor ? "mentor" : "entrepreneur"}
                    size="sm"
                    showRemoveOption
                  />
                )}

                {/* Legacy connection status buttons removed — ConnectionButton handles all states */}

                {/* Mentorship request buttons */}
                {canRequestMentorship && !mentorshipRequestStatus && onRequestMentorship && hasCompletedFoundations && (
                  <Button size="sm" variant="outline" onClick={onRequestMentorship}>
                    <BookOpen className="h-4 w-4 mr-1" />
                    Request Mentorship
                  </Button>
                )}
                {canRequestMentorship && !mentorshipRequestStatus && !hasCompletedFoundations && (
                  <Button size="sm" variant="outline" disabled className="text-muted-foreground">
                    <BookOpen className="h-4 w-4 mr-1" />
                    Complete Course First
                  </Button>
                )}
                {mentorshipRequestStatus === "pending" && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      Request Pending
                    </Badge>
                    {mentorshipRequestId && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        Cancel Request
                      </Button>
                    )}
                  </div>
                )}
                {mentorshipRequestStatus === "accepted" && hasActiveMentorship && (
                  <Badge className="bg-green-500 text-white">
                    Mentorship Active
                  </Badge>
                )}

                {/* Mentor-initiated: Offer Mentorship button (self-contained) */}
                {member.role?.toLowerCase() === "entrepreneur" && (
                  <MentorInitiatedRequestButton
                    entrepreneurId={member.id}
                    entrepreneurName={member.full_name}
                    entrepreneurAvatarUrl={member.avatar_url}
                    size="sm"
                  />
                )}
              </div>
            </div>

            <UserBadgesSection userId={member.id} />

            {/* Bio Section */}
            {member.bio && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">About</h3>
                <p className="text-sm">{member.bio}</p>
              </div>
            )}

            {/* Professional Summary - Mentor */}
            {isMentor && member.mentor_profile?.professional_background && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Professional Background</h3>
                <p className="text-sm">{member.mentor_profile.professional_background}</p>
              </div>
            )}

            {/* Career Highlights - Mentor (shown for all users) */}
            {isMentor && member.mentor_profile?.career_highlights && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Career Highlights</h3>
                <p className="text-sm">{member.mentor_profile.career_highlights}</p>
              </div>
            )}

            {/* Company Description - Entrepreneur */}
            {isEntrepreneur && member.entrepreneur_profile?.company_description && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Company Description</h3>
                <p className="text-sm">{member.entrepreneur_profile.company_description}</p>
              </div>
            )}

            {/* Industries */}
            {member.industries && member.industries.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Industries</h3>
                <div className="flex flex-wrap gap-2">
                  {member.industries.map((industry, i) => (
                    <Badge key={i} variant="outline">
                      {industry.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Skills / Areas of Expertise */}
            {member.skills && member.skills.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Areas of Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {member.skills.map((skill, i) => (
                    <Badge key={i} variant="secondary">
                      {skill.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Private Information Placeholder - Show when not connected and not admin */}
            {!isOwnProfile && !showAllFields && !isConnected && !connectionLoading && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Additional Information
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <LockedField label="Contact Information" />
                    <LockedField label="Location" />
                    <LockedField label="Education Level" />
                    <LockedField label="Company Details" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    🔒 Connect to view this information
                  </p>
                </div>
              </>
            )}

            {/* Contact Information - Only shown when permitted via shares or admin */}
            {showContactInfo && hasContactAccess && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">Contact</h3>
                  {contactLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ) : (contactInfo.email || contactInfo.linkedin_url || contactInfo.phone) ? (
                    <div className="space-y-2">
                      {contactInfo.email && (
                        <a
                          href={`mailto:${contactInfo.email}`}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Mail className="h-4 w-4" />
                          {contactInfo.email}
                        </a>
                      )}
                      {contactInfo.phone && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          {contactInfo.phone} {contactInfo.phone_type && `(${contactInfo.phone_type})`}
                        </p>
                      )}
                      {contactInfo.linkedin_url && (
                        <a
                          href={contactInfo.linkedin_url.startsWith('http') ? contactInfo.linkedin_url : `https://${contactInfo.linkedin_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Linkedin className="h-4 w-4" />
                          LinkedIn Profile
                        </a>
                      )}
                      {contactInfo.parish && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          {contactInfo.parish}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No contact information shared</p>
                  )}
                </div>
              </>
            )}

            {/* Admin-only fields */}
            {showAllFields && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Profile Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {contactInfo.email && (
                      <div>
                        <span className="text-muted-foreground">Email:</span>
                        <p>{contactInfo.email}</p>
                      </div>
                    )}
                    {contactInfo.linkedin_url && (
                      <div>
                        <span className="text-muted-foreground">LinkedIn:</span>
                        <a href={contactInfo.linkedin_url.startsWith('http') ? contactInfo.linkedin_url : `https://${contactInfo.linkedin_url}`} target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline truncate">
                          {contactInfo.linkedin_url}
                        </a>
                      </div>
                    )}
                    {contactInfo.phone && (
                      <div>
                        <span className="text-muted-foreground">Phone:</span>
                        <p>{contactInfo.phone} {contactInfo.phone_type && `(${contactInfo.phone_type})`}</p>
                      </div>
                    )}
                    {contactInfo.parish && (
                      <div>
                        <span className="text-muted-foreground">Parish:</span>
                        <p>{contactInfo.parish}</p>
                      </div>
                    )}
                    {member.created_at && (
                      <div>
                        <span className="text-muted-foreground">Registration Date:</span>
                        <p>{format(new Date(member.created_at), "MMM d, yyyy")}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Demographics (Internal Only)</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {member.gender && (
                      <div>
                        <span className="text-muted-foreground">Gender:</span>
                        <p>{member.gender}</p>
                      </div>
                    )}
                    {member.age_range && (
                      <div>
                        <span className="text-muted-foreground">Age Range:</span>
                        <p>{member.age_range}</p>
                      </div>
                    )}
                    {member.education_level && (
                      <div>
                        <span className="text-muted-foreground">Education Level:</span>
                        <p>{getEducationLabel(member.education_level)}</p>
                      </div>
                    )}
                    {member.has_device && (
                      <div>
                        <span className="text-muted-foreground">Device Access:</span>
                        <p>{member.has_device}</p>
                      </div>
                    )}
                    {member.referred_by && (
                      <div>
                        <span className="text-muted-foreground">Referred By:</span>
                        <p>{member.referred_by}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Role-specific details for admin */}
                {isMentor && member.mentor_profile && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3">Mentor Details</h3>
                    <div className="space-y-2 text-sm">
                      {member.mentor_profile.availability_notes && (
                        <div>
                          <span className="text-muted-foreground">Availability:</span>
                          <p>{member.mentor_profile.availability_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isEntrepreneur && member.entrepreneur_profile && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3">Entrepreneur Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {member.entrepreneur_profile.startup_stage && (
                        <div>
                          <span className="text-muted-foreground">Company Stage:</span>
                          <p>{getCompanyStageLabel(member.entrepreneur_profile.startup_stage)}</p>
                        </div>
                      )}
                      {member.entrepreneur_profile.target_market && (
                        <div>
                          <span className="text-muted-foreground">Target Market:</span>
                          <p>{member.entrepreneur_profile.target_market}</p>
                        </div>
                      )}
                      {member.entrepreneur_profile.funding_status && (
                        <div>
                          <span className="text-muted-foreground">Funding Status:</span>
                          <p>{member.entrepreneur_profile.funding_status}</p>
                        </div>
                      )}
                      {member.entrepreneur_profile.specific_challenges && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Specific Challenges:</span>
                          <p>{member.entrepreneur_profile.specific_challenges}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {isAdmin && (
              <div className="border rounded-md p-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role Capabilities</p>

                {/* Mentor row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Mentor</span>
                    {member?.is_mentor
                      ? <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                      : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inactive</span>
                    }
                    {member?.role === 'mentor' && (
                      <span className="text-xs text-muted-foreground">(primary role)</span>
                    )}
                  </div>
                  <div>
                    {member?.role === 'mentor' ? (
                      <span className="text-xs text-muted-foreground italic">Cannot revoke primary role</span>
                    ) : member?.is_mentor ? (
                      confirmRevokeRole === 'mentor' ? (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole(null)}>Cancel</Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={revokeDualRole.isPending}
                            onClick={() => revokeDualRole.mutate({ userId: member.id, roleToRevoke: 'mentor' }, { onSuccess: () => setConfirmRevokeRole(null) })}
                          >
                            {revokeDualRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Revoke'}
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole('mentor')}>
                          Revoke Mentor
                        </Button>
                      )
                    ) : (
                      <Button
                        size="sm"
                        disabled={grantDualRole.isPending}
                        onClick={() => grantDualRole.mutate({ userId: member.id, roleToGrant: 'mentor' })}
                      >
                        {grantDualRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Grant Mentor'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Entrepreneur row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Entrepreneur</span>
                    {member?.is_entrepreneur
                      ? <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                      : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inactive</span>
                    }
                    {member?.role === 'entrepreneur' && (
                      <span className="text-xs text-muted-foreground">(primary role)</span>
                    )}
                  </div>
                  <div>
                    {member?.role === 'entrepreneur' ? (
                      <span className="text-xs text-muted-foreground italic">Cannot revoke primary role</span>
                    ) : member?.is_entrepreneur ? (
                      confirmRevokeRole === 'entrepreneur' ? (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole(null)}>Cancel</Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={revokeDualRole.isPending}
                            onClick={() => revokeDualRole.mutate({ userId: member.id, roleToRevoke: 'entrepreneur' }, { onSuccess: () => setConfirmRevokeRole(null) })}
                          >
                            {revokeDualRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Revoke'}
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole('entrepreneur')}>
                          Revoke Entrepreneur
                        </Button>
                      )
                    ) : (
                      <Button
                        size="sm"
                        disabled={grantDualRole.isPending}
                        onClick={() => grantDualRole.mutate({ userId: member.id, roleToGrant: 'entrepreneur' })}
                      >
                        {grantDualRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Grant Entrepreneur'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>

      {/* Cancel Mentorship Request Dialog */}
      {mentorshipRequestId && (
        <CancelMentorshipRequestDialog
          open={cancelDialogOpen}
          onOpenChange={(open) => {
            setCancelDialogOpen(open);
            if (!open && onCancelMentorshipRequest) {
              onCancelMentorshipRequest();
            }
          }}
          requestId={mentorshipRequestId}
          mentorName={member?.full_name}
        />
      )}
    </Dialog>
  );
}
